import * as NextJS from 'next'

export enum VERSION {
	TREKKING = 'TREKKING',
	PEAKS = 'PEAKS',
}

// todo: rename trek to trip
export namespace Trek {
	export type Itinerary = {
		description: string
		days?: {
			dayNum?: number
			title: string
			description?: string
			totalElevationChange?: number
			destinationElevation?: number
			totalTime?: number
			distance?: number
		}[]
		climbSections?: {
			name: string
			elevation: number
			isSummit: boolean
			isBaseCamp: boolean
			description?: string
		}[]
	}

	export type MonthlyWeatherData = {
		monthNo?: number
		temperature: number
		rainfall: number
	}

	export type Weather = {
		description: string
		monthlyWeatherData?: MonthlyWeatherData[]
	}

	export type Gear = {
		description: string
	}

	export type Accommodation = {
		description: string
		types: string[]
		pictures?: number[]
	}

	export type Permits = {
		description: string
		permits: {
			name: string
			cost: number
			costLocal?: number
			description: string
			availability?: string
		}[]
	}

	export type Access = {
		description: string
		pictures?: number[]
	}

	export type Route = {
		description?: string
		locations: {
			name: string
			longitude: number
			latitude: number
			type: App.Map.IconType | App.Map.RouteLineType
		}[]
		routeLines: {
			type: string
			linePoints: [number, number][]
		}[]
	}

	export type Variations = {
		name: string
		pictures: number[]
		description: string // html
		daysExtra: [number] | [number, number]
	}

	export type FAQ = {
		question: string
		answer: string
	}

	export type TrekRoute = {
		startPlace: string // (where walk in begins/ends)
		endPlace: string
		// access: string // bus, plane, drive etc
		// todo: make an enum?
		routeType: string // (out and back, circuit, through hike)
	}

	export type ClimbRoute = {
		startPlace: string // (base for the climb)
		endPlace: string
		howManyDaysTotal: [number, number] | [number] | [] // could be derived from walk in + walk out + climb days
		totalGainFromStart: number
		// todo: also enumable?
		approach: string[] // 'walk in' or 'drive' or 'helicopter' or 'fly'
	}

	export type WhenToGo = {
		bestMonths: number[]
		note?: string
	}

	export type HighLevelInfo = {
		trekRoute?: TrekRoute
		climbRoute?: ClimbRoute
		// distances only if TREK
		distances?: {
			total: {
				distance: [number, number] | [number] | []
				days: [number, number] | [number] | []
				gain: number
				highestPoint: number
			}
			daily: {
				time: [number, number] | []
				distance: [number, number] | []
				elevationGain: [number, number] | []
			}
		}
		// OPTIONAL, AND ONLY IF CLIMB
		walkIn?: {
			distanceOneWay: number
			totalGain: number
			numDaysIn: [number, number] | []
			numDaysOut: [number, number] | []
		}
		// ONLY IF CLIMB
		climb?: {
			maxElevation: number
			howManyDays: [number, number] | [number] | [] // how many days from/to base camp does it take
			elevationGainFromBaseCamp: number
			numberOfHighCamps: number // (eg 4 for ama dablam - but not all always used/needed)
			highCampNote?: string // eg 'but typically not used' (island peak) or 'but most use just 2-3' (ama dablam)
		}
		cost?: {
			solo: {
				range: [number, number] | []
			}
		}
		whenToGo?: WhenToGo
		guideNeeded?: {
			required: boolean
			description: string
		}
		// routeType?: string
		// accommodation?: string[]
	}

	export type GuideLink = {
		agencyName: string
		link: string
		cost: number
		costUpper?: number
		linkId?: string
	}

	export type TrekDifficulty = {
		individualScores: {
			distance: number
			elevationGain: number
			terrain: number
			altitude: number
			climaticConditions: number
			technicalSkills: number
			supportFacilities: number
		}
		total: number
		average: number
	}

	export type SimpleDifficulty = 1 | 2 | 3 | 4 | 5 | 6 | 7

	export type ClimbDifficulty = {
		simple: SimpleDifficulty[]
	}

	export type TripJSONData = {
		// required
		// name: string
		trekOrClimb: App.DataTypes.TrekOrClimb // 'TREK' | 'CLIMB'
		description: string
		history: string
		maxElevation: number
		alternativeNames?: string[]

		lengthInDays: number
		countries: string[]
		continent: string

		trekDifficulty?: TrekDifficulty

		climbDifficulty?: ClimbDifficulty

		highLevelInfo?: HighLevelInfo
		itinerary?: Itinerary
		weather?: Weather
		gear?: Gear
		services?: {
			description: string

			accommodation?: string
			food?: string
			guides?: string

			porters?: string
			medical?: string
			gear?: string

			transport?: string
			communications?: string
			atms?: string

			shops?: string
		}
		accommodation?: Accommodation
		route?: Route
		permits?: Permits
		access?: Access
		videos?: string[]
		variations?: Variations[]
		similar?: string[] // array of slugs
		faqs?: FAQ[]
		groups?: string[]
		guidedLinks: GuideLink[]
	}

	export type TrekStatus = DB.Models.TripAttributes & {
		missingRequiredHoldingFields: MissingField[]
		missingRequiredFields: MissingField[]
		missingNonRequiredFields: MissingField[]
		deltas: { draft: TripJSONData; live: TripJSONData } | undefined
	}

	export type AdminTrekStatus = {
		trip: DB.AdminPageTrek
		missingRequiredHoldingFields: MissingField[]
		missingRequiredFields: MissingField[]
		missingNonRequiredFields: MissingField[]
	}

	export type AdminSimplifiedTrekStatus = {
		trip: DB.SimplifiedTripForAdminPage
		draft: {
			missingRequiredHoldingFields: MissingField[]
			missingRequiredFields: MissingField[]
			missingNonRequiredFields: MissingField[]
			isValidHoldingPage: boolean
			isValidCompletePage: boolean
			isContentDifferentToPublic: boolean
			isUploadCountDifferentToPublic: boolean
		}
		public: {
			missingRequiredHoldingFields: MissingField[]
			missingRequiredFields: MissingField[]
			missingNonRequiredFields: MissingField[]
			isValidHoldingPage: boolean
			isValidCompletePage: boolean
		}
	}

	export enum EditSection {
		INFO = 'INFO',
		GUIDES = 'GUIDES',

		ROUTE_INFO = 'ROUTE_INFO',
		MORE_INFO = 'MORE_INFO',
		WHEN_TO_GO = 'WHEN_TO_GO',
		ITINERARY = 'ITINERARY',
		WEATHER = 'WEATHER',

		GEAR = 'GEAR',
		SERVICES = 'SERVICES',
		ACCOMMODATION = 'ACCOMMODATION',
		ROUTE_MAP = 'ROUTE_MAP',
		PERMITS = 'PERMITS',

		ACCESS = 'ACCESS',
		VIDEOS = 'VIDEOS',
		GROUPS = 'GROUPS',
		VARIATIONS = 'VARIATIONS',
		SIMILAR = 'SIMILAR',

		FAQS = 'FAQS',
		PICTURES = 'PICTURES',
		DIFFICULTY = 'DIFFICULTY',
	}

	export type MissingField = {
		field: string
		editSection: EditSection
	}
}

export type TrekMissingData = {
	name: string
	missingFields: string[]
}

// export type TreksFromCSV = {
// 	valid: Trek[]
// 	invalid: TrekMissingData[]
// }

type LatLon = {
	lat: number
	lng: number
}

export type MapBounds = {
	_ne: LatLon
	_sw: LatLon
}

export type SearchFilter = {
	text: string
	timeOfYear: number[]
	countryFilters: string[]
	continentFilters: string[]
	mapBounds?: MapBounds
	duration?: string
	heightFilter?: number
	isWithoutPermit: boolean
	routeTypeFilter?: 'TREK' | 'CLIMB'
	budgetFilter?: number
	simpleDifficultyFilter?: number[]
	trekkingDifficultyFilter?: number[]
}

export type ValidTrekMissingData = {
	name: string
	missingFields: string[]
}

export type ThumbSize = {
	name: string
	width?: number
	height?: number
}

export interface AppState {
	unit: string // todo: enum, and elsewhere
	userAuthStatusIsKnown: boolean
	user?: App.PseudoUser
	editFeedbackSection?: App.DataTypes.FeedbackSection
	signUpOpen: boolean
	logInOpen: boolean
}

export namespace App {
	export interface Context extends NextJS.NextPageContext {
		user?: { id: number }
	}

	export namespace DataTypes {
		export enum TrekOrClimb {
			TREK = 'TREK',
			CLIMB = 'CLIMB',
		}
		export enum UserRole {
			USER = 'USER',
			GUIDE = 'GUIDE',
			ADMIN = 'ADMIN',
			SUPER_ADMIN = 'SUPER_ADMIN',
		}
		export enum TripVersion {
			DRAFT = 'DRAFT',
			LIVE = 'LIVE',
		}
		export enum ResourceType {
			UNIQUE_TRIP = 'UNIQUE_TRIP',
			ARTICLE = 'ARTICLE',
			GUIDE = 'GUIDE',
			CERTIFICATE = 'CERTIFICATE',
		}
		export enum FeedbackSection {
			HIGH_LEVEL_INFO = 'HIGH_LEVEL_INFO',
			GUIDES = 'GUIDES',
			ITINERARY = 'ITINERARY',
			ROUTE_MAP = 'ROUTE_MAP',
			WEATHER = 'WEATHER',
			GEAR = 'GEAR',
			SERVICES = 'SERVICES',
			VIDEOS = 'VIDEOS',
			SIMILAR = 'SIMILAR',
			VARIATIONS = 'VARIATIONS',
			ACCESS = 'ACCESS',
			ACCOMMODATION = 'ACCOMMODATION',
			GROUPS = 'LINKS',
			PERMITS = 'PERMITS',
			SUMMIT_VIEW = 'SUMMIT_VIEW',
			FAQs = 'FAQs',
			DIFFICULTY = 'DIFFICULTY',
			PICTURES = 'PICTURES',
			SUGGEST_ROUTE = 'SUGGEST_ROUTE',
			HISTORY = 'HISTORY',
		}

		export namespace Reviews {
			export type Provider = 'tripadvisor' | 'google' | 'trustpilot'

			type ReviewJSON = {
				rating: {
					value: number
				}
				timestamp: string
				review_text: string
			}
			export type GoogleReviewJSON = ReviewJSON & {
				// google only fields
				owner_answer?: string
				review_id: string // unique id
				review_url: string
			}
			export type TripAdvisorReviewJSON = ReviewJSON & {
				title: string
				responses: {
					title: string
					text: string
				}[]
				url: string // unique id
			}
			export type TrustPilotReviewJSON = ReviewJSON & {
				title: string
				url: string // unique id
			}
		}
	}

	export namespace Types {
		export type CountrySummary = {
			isoCode: string
			continent: string
			peakCount: number
			cheapestPrice: number | null
			lowestHeight: number | null
			highestHeight: number | null
			shortestTrek: number | null
			longestTrek: number | null
			difficulties: number[]
			tallestPeakUploadId: number | null
		}

		export type CarouselTrip = {
			id: number
			name: string
			blurb: string
			slug: string
			continent: string
			countries: string[]
			// images: API.Upload[]
			firstUploadId: number
			difficulty?: number[]
			lowestPrice: number | null
			detail: string
		}

		export type CarouselCollection = {
			title: string
			blurb: string
			trips: HomePageTrip[]
		}

		export type HomePagePeakLocation = {
			latitude: number
			longitude: number
			height: number | null
		}

		export type PeakMeta = {
			lowestPeak: number
			highestPeak: number
			difficulties: number[]
			continentCount: number
			countryCount: number
			tripCount: number
			expedCount: number
		}

		export type TrekMeta = {
			tripCount: number
			shortestDays: number
			longestDays: number
			continentCount: number
			countryCount: number
			difficulties: {
				lowest: number
				highest: number
			}
		}

		export interface ContinentSummary {
			continent: string
			countries: CountrySummary[]
			blurb: string
		}

		export type MountainReview = {
			mountainName: string
			reviewText: string
			reviewPercent: number
			provider: string
			companyName: string
			companySlug: string
		}

		export type HomePageData = {
			trips: App.HomePageTrip[]
			guideCount: number
			reviewCount: number
			guideInfo: {
				logoId: number
				slug: string
				name: string
			}[]
			carouselTrips: CarouselCollection[]
			// countrySummaries: Record<string, CountrySummary[]>
			continentSummaries: ContinentSummary[]
			peakMeta: PeakMeta | null
			peakLocations: HomePagePeakLocation[]
			uniqueGuideLanguages: string[]
			trekMeta: TrekMeta | null
			latestArticles: API.Article[]
			summitPictures: DB.Models.UploadAttributes[]
			mountainReviews: MountainReview[]
		}

		export type GuideProfilePageData = {
			guide: API.GuideCompany
			version: VERSION
		}

		export type PossibleImprovement = {
			value: string
			category: 'TRIP' | 'PROFILE'
		}

		export type GuideScore = {
			score: number
			possibleImprovements: PossibleImprovement[]
		}

		export type ReviewProviderState = {
			status: string
			reviewCount?: number
		}
		export type ReviewProviderStates = {
			tripadvisor: ReviewProviderState
			google: ReviewProviderState
			trustpilot: ReviewProviderState
		}

		export type CountryPage = {
			version: VERSION
			trips: DB.Models.TripAttributes[]
			country: string
			countryRecord: API.Country | null
			countryISOCode: string
			guides: API.GuideCompanyOverview[]
		}

		export type BestGuidesPageData = {
			id: number
			name: string
			slug: string
			guideCount: number
			topGuides: Array<{
				companyName: string
				companyId: number
				reviewScore: number
				totalReviewCount: number
			}>
			matchingReviews: Array<{
				companyName: string
				companyId: number
				provider: string
				reviewScore: number
				reviewText: string
			}>
		}
	}

	export type TrekUpdate = {
		description: string
		trekOrClimb: DataTypes.TrekOrClimb
		alternativeNames: string
	}

	export type WhenToGoUpdate = {
		note: string
		bestMonths: string
	}

	export type UploadUpdate = {
		source: string
		attributionText: string
		attributionLink: string
		isDisplayedInGallery: boolean
	}

	export type TripPublishedPropertiesUpdate = {
		isApproved?: boolean
		publishedFrom?: string | null
	}

	export type Trek = {
		id: number
		name: string
		slug: string
		alternativeNames: string
		trekOrClimb: DataTypes.TrekOrClimb
		description: string
		countries: string[]
		continent: string
		thumbSlugs: string[]
		// todo: type properly
		similarTreks: string[]
		whenToGo: {
			note: string
			bestMonths: string
		} | null
	}

	export type TripCardProps = {
		name: string
		trekOrClimb: DataTypes.TrekOrClimb
		countries: string[]
		continent: string
		slug: string
		lowestCost?: number
		totalDistanceLength?: [number, number] | [number] | []
		totalDaysLength?: [number, number] | [number] | []
		height?: number
		firstUploadId: number
		trekDifficulty?: number
		climbDifficulty: [number, number] | [number] | []
		isValidCompletePage: boolean
	}

	// todo: what's this? obsolete?
	export type HomepageTrek = Pick<
		Trek,
		| 'name'
		| 'slug'
		| 'trekOrClimb'
		| 'description'
		| 'thumbSlugs'
		| 'countries'
		| 'continent'
	>

	export namespace Map {
		export const PossibleRouteLineTypews = [
			`trek-route`,
			`climb-route`,
			`side-trip`,
			`walk-in`,
		]
		export type RouteLineType = typeof PossibleRouteLineTypews[number]

		export const PossibleIconTypes = [
			'stop',
			'airport',
			'turn-back',
			'high-point',
			'side-trip',
			'drive',
			'base-camp',
			'high-camp',
			'summit',
			'start-flag',
			'end-flag',
			'start-end-flag',
		]
		export type IconType = typeof PossibleIconTypes[number]
	}

	export type HomePageTrip = Omit<
		DB.HomePageTrip,
		| 'jsonDataTrekOrClimb'
		| 'jsonDataHighLevelInfo'
		| 'jsonDataCountries'
		| 'jsonDataContinent'
		| 'jsonDataGuidedLinks'
		| 'height'
		| 'jsonDataDistances'
		| 'jsonDataTrekDays'
		| 'jsonDataClimbDays'
		| 'jsonDataBestMonths'
		| 'jsonDataPermits'
		| 'jsonDataRouteLocationsAll'
		| 'jsonTrekDifficulty'
		| 'jsonDataClimbDifficulty'
	> & {
		lowestCost: number | null
		totalDistanceLength: [number]
		totalDaysLength?: [number]
		height: number | null
		lengthInDays?: number
		countries: string[]
		continent: string
		bestMonths: string[]
		permitsNeeded: boolean
		routeLocation: { latitude: number; longitude: number }
		trekDifficulty: number | null
		climbDifficulty?: number[]
	}

	export type ExpeditionListTrip = HomePageTrip & {
		description: string
	}

	export type PseudoUser = {
		id: number
		role: DataTypes.UserRole
	}

	export type User = {
		id: number
		role: DataTypes.UserRole
		email: string
		accountDetails?: Record<string, any>
	}

	export type RevalidationResponse = {
		success: boolean
		note?: string
		error?: string
	}

	export type RevalidateOnPeaksResponse = {
		success: boolean
		note?: string
		error?: string
	}

	export type ArticleJSONData = {
		subtitle: string
		content: string
		seoDescription: string
		coverImage: number | undefined
		tags: string[]
		customStyles?: string
		isFeaturedArticle?: boolean
	}

	export type CountryJSONData = {
		blurb: string
		mainRegionsSummary: string
		highlights: {
			mostPopular: number[]
			lesserKnown: number[]
			mostDifficult: number[]
		}
		whenToClimb: string
	}

	export type GuideJSONProfile = {
		website?: string
		mainLocation?: string
		phoneNumber?: string
		email?: string
		tripAdvisorReviewURL?: string
		googleMapsReviewURL?: string
		trustPilotReviewURL?: string
		businessWhatsappNumber?: string
		certificationsBlurb?: string
		spokenLanguages?: string[]
		aboutInfoBlurb?: string
		youtubeVideo?: string
		youtubeChannel?: string
		instagramHandle?: string
		facebookHandle?: string
		tiktokHandle?: string
		logoUploadId?: number
		coverImageUploadId?: number
		yearStarted?: number
		discounts?: string
		aiReviewSummary?: string
	}

	export type GuideTripDetailsJSON = {
		shortBlurb?: string
		videos?: string[]
		typicalGroupSize?: number[]
		climberToGuideRatio?: number[]
		lengtOfTripInDays?: number[]
		includesPorters?: boolean | null
		includesGear?: boolean | null
		womanOnlyTripsAvailable?: boolean | null
	}

	export type TripOffering = App.HomePageTrip & {
		offering?: API.GuideTrip
	}

	export type GuideTripInsights = {
		tripId: number
		currentPrice: number
		medianPrice: number
		lowestPrice: number
		highestPrice: number
		otherGuidesCount: number
		deltaFromLowest: number
		deltaFromMedian: number
	}

	export type GuideInsights = {
		highLevelStats: {
			priceDifferenceToLowest: number
			priceDifferenceToMedian: number
		}
		tripInsights: GuideTripInsights[]
	}
}
export namespace DB {
	export namespace Models {
		export interface TripAttributes {
			id: number
			uniqueTripId: number
			version: App.DataTypes.TripVersion
			name: string
			slug: string
			jsonData: Trek.TripJSONData
			isValidHoldingPage: boolean
			isValidCompletePage: boolean
			isApproved: boolean
			publishedFrom?: string | null
			uploads?: UploadAttributes[]
			similarTrips?: TripAttributes[]
			guideTrips?: API.GuideTrip[]
			hasTopGuidesPage?: boolean
		}

		export interface TrekWhenToGoAttributes {
			id: number
			trekId: number
			note: string
			bestMonths: string
		}

		export interface UploadAttributes {
			id: number
			resourceId: number | undefined
			resourceType: App.DataTypes.ResourceType
			version: App.DataTypes.TripVersion
			filename: string
			uploadedBy: number
			source: string
			attributionText: string
			attributionLink: string
			isDisplayedInGallery: boolean
			dimensionsOriginal: string
			dimensionsSmall: string
			dimensionsMedium: string
			dimensionsLarge: string
			displayOrder: number
			isApproved: boolean
			user?: {
				guideCompany?: {
					id: number
					companyName: string
				}
			} | null
		}

		export interface UserAttributes {
			id: number
			email: string
			password: string
			role: App.DataTypes.UserRole
			accountDetails: Record<string, any>
		}

		export interface GuideCompanyAttributes {
			id: number
			userId: number | null
			companyName: string
			slug: string
			aiReviewSummary?: string | null
			jsonProfile: App.GuideJSONProfile
			isOwned: boolean
			inviteCount: number
			certificates?: CertificateAttributes[] | API.Types.GuideCertificate[]
			companyReviewData?: API.Types.CompanyReviewData | null
			reviewProviderStates?: App.Types.ReviewProviderStates
		}

		export interface LinkClickAttributes {
			id: number
			tripId: number
			guideTripIdClicked: number
			guideTripMultiPeakIdClicked: number
			guideCompanyIdProfileClicked: number
			linkTarget: string
			ip: string
			time: string
		}

		export interface PasswordResetAttributes {
			id: number
			userId: number
			token: string
			expiry: string
		}

		export interface ArticleAttributes {
			id: number
			version: VERSION
			title: string
			slug: string
			draftJson: App.ArticleJSONData
			liveJson: App.ArticleJSONData
			isPublished: boolean
			createdAt?: string
			updatedAt?: string
		}

		export interface CountryAttributes {
			id: number
			version: VERSION
			isoCode: string
			slug: string
			json: App.CountryJSONData
			isPublished: boolean
		}

		export interface GuideTripAttributes {
			id: number
			guideCompanyId: number
			guideCompany?: GuideCompanyAttributes | null
			tripId: number
			price: number
			currency: string
			webLink: string
			jsonDetails: App.GuideTripDetailsJSON
		}

		export interface GuideTripMultiPeakAttributes {
			id: number
			guideCompanyId: number
			guideCompany?: GuideCompanyAttributes
			price: number
			currency: string
			webLink: string
		}

		export interface GuideTripMultiPeakTripAttributes {
			id: number
			guideTripMultiPeakId: number
			tripId: number
		}

		export interface CertificateAttributes {
			id: number
			guideCompanyId: number
			uploadId: number
			name: string
			adminNote: string
			status: 'PENDING' | 'REJECTED' | 'APPROVED'
		}

		export interface CompanyReviewDataAttributes {
			id: number
			guideCompanyId: number
			totalReviewAverage?: number | null
			totalReviewCount?: number | null
			taReviewAverage?: number | null
			taReviewCount?: number | null
			tpReviewAverage?: number | null
			tpReviewCount?: number | null
			goReviewAverage?: number | null
			goReviewCount?: number | null
			createdAt?: Date
			updatedAt?: Date
		}

		export interface CompanyReviewAttributes {
			id: number
			guideCompanyId: number
			provider: 'tripadvisor' | 'google' | 'trustpilot'
			uid: string
			jsonReviewBlob: Record<string, any>
			reviewPercent: number
			createdAt?: Date
			updatedAt?: Date
		}

		export interface ReviewProviderImportTaskAttributes {
			id: number
			guideCompanyId: number
			provider: 'google' | 'tripadvisor' | 'trustpilot'
			completed: boolean
			createdAt?: Date
			updatedAt?: Date
		}
	}

	export type AdminPageTrek = Pick<
		Models.TripAttributes,
		| 'id'
		| 'uniqueTripId'
		| 'version'
		| 'name'
		| 'slug'
		| 'jsonData'
		| 'isValidHoldingPage'
		| 'isValidCompletePage'
		| 'isApproved'
		| 'publishedFrom'
	> & {
		similarCount: number
		uploadsCount: number
		publicGuideLinksCount: number
	}

	export type SimplifiedTripForAdminPage = {
		name: string
		slug: string
		uniqueTripId: number
		draftId: number
		publicId: number
		draftJsonData: Trek.TripJSONData
		publicJsonData: Trek.TripJSONData
		isApproved: boolean
		publishedFrom: string | null
		draftIsValidCompletePage: boolean
		publicIsValidCompletePage: boolean
		draftIsValidHoldingPage: boolean
		publicIsValidHoldingPage: boolean
		draftUploadsCount: number
		publicUploadsCount: number
		publicGuideLinksCount: number
	}

	export type HomePageTrip = Pick<
		Models.TripAttributes,
		'id' | 'uniqueTripId' | 'name' | 'slug' | 'isValidCompletePage'
	> & {
		firstUploadId: number
		trekOrClimb: App.DataTypes.TrekOrClimb
		description: string
		jsonDataHighLevelInfo: Partial<Trek.TripJSONData>
		jsonDataCountries: string
		jsonDataContinent: string
		jsonDataGuidedLinks: string
		height?: number
		jsonDataDistances: string
		jsonDataTrekDays: string
		jsonDataClimbDays: string
		jsonDataBestMonths: string
		jsonDataPermits: string
		// jsonDataRouteLocation: string
		jsonDataRouteLocationsAll: string
		jsonDataClimbDifficulty: string
		jsonTrekDifficulty: string
		lowestCost: number
	}
}

export namespace API {
	export type PaginationInput = {
		page: number
		limit: number
	}
	export type PaginationInfo = {
		totalPages: number
		totalItems: number
		page: number
		perPage: number
		hasNextPage: boolean
		hasPreviousPage: boolean
	}
	export namespace Response {
		export type Auth = {
			token?: string
			error?: string
			user?: App.PseudoUser
		}

		export type VerifyToken = {
			isValid: boolean
			user?: App.PseudoUser
		}

		export type CreateAdminUser = {
			success: boolean
			error?: string
		}

		export type UploadUserFile = {
			success: boolean
			error?: string
			myUploads?: API.UserUpload[]
			newUploadId?: number
		}

		export type SignedURLResponse = {
			success: boolean
			signedURL: string
			key: string
		}

		export type AdminUploadsResponse = {
			items: PendingUpload[]
		}

		export type RebuildPageResponse = {
			success: boolean
			error?: string
		}

		export type UpdateTrekResponse = {
			updatedTrek?: Trek.TrekStatus
			// cacheRebuild: App.RevalidationResponse
		}

		export type UpdateTripPublishedData = {
			success: boolean
			// cacheRebuild: App.RevalidationResponse
		}

		export type PromoteDraftResponse = {
			success: boolean
			// cacheRebuild: App.RevalidationResponse
		}

		export type UploadFileResponse = {
			operation: {
				success: boolean
				error?: string
				trek?: DB.Models.TripAttributes
			}
			// cacheRebuild?: App.RevalidationResponse
		}

		export type ImportFileResponse = {
			operation: {
				success: boolean
				error?: string
				trek?: DB.Models.TripAttributes
			}
			// cacheRebuild?: App.RevalidationResponse
		}

		export type DeleteFileResponse = {
			operation: {
				success: boolean
				error?: string
				// trek?: DB.Models.TripAttributes
			}
			// cacheRebuild?: App.RevalidationResponse
		}

		export type UpdateUploadResponse = {
			operation: boolean
			// cacheRebuild?: App.RevalidationResponse
		}

		export type UpdateUploadUIPropertiesResponse = {
			operation: boolean
			// cacheRebuild?: App.RevalidationResponse
		}

		export type ApprovePendingUploadResponse = {
			operation: boolean
			// cacheRebuild?: App.RevalidationResponse
		}

		export type CreateArticleResponse = {
			success: boolean
		}

		export type GetArticlesResponse = {
			success: boolean
			collection: {
				items: API.Article[]
			}
		}

		export type GetArticleResponse = {
			success: boolean
			article: API.Article | undefined
		}

		export type UpdateArticleResponse = {
			success: boolean
			updatedArticle?: API.Article
			// cacheRebuild?: App.RevalidationResponse
		}

		export type CreateCountryResponse = {
			success: boolean
			error?: string
		}

		export type GetCountriesResponse = {
			success: boolean
			collection: {
				items: API.Country[]
			}
		}

		export type GetCountryResponse = {
			success: boolean
			country: API.Country | undefined
		}

		export type UpdateCountryResponse = {
			success: boolean
			updatedCountry?: API.Country
		}

		export type CreateGuideTrip = {
			success: boolean
			error?: string
			newGuideTrips: GuideTrip[]
			guideScore?: App.Types.GuideScore
		}

		export type RemoveGuideTrip = {
			success: boolean
			error?: string
			newGuideTrips: GuideTrip[]
			guideScore?: App.Types.GuideScore
		}

		export type UpsertGuideTripMultiPeak = {
			success: boolean
			error?: string
			newTrips: GuideTripMultiPeak[]
		}

		export type RemoveGuideTripMultiPeak = {
			success: boolean
			error?: string
			newTrips: GuideTripMultiPeak[]
		}

		export type GetGuideProfile = {
			record: DB.Models.GuideCompanyAttributes | undefined
			guideScore?: App.Types.GuideScore
			reviewProviderStates?: App.Types.ReviewProviderStates
		}
	}

	export namespace Input {
		export type UpdateTrek = {
			id: number
			name?: string
			slug?: string
			jsonData: Partial<Trek.TripJSONData>
			similarTripIds?: number[]
		}

		export type RebuildPageInput = {
			uniqueTripId: number
			rebuildHomepageToo?: boolean
			token: string
		}

		export type CreateGuideTripInput = {
			// guideCompanyId: number
			tripId: number
			price: number
			currency: string
			webLink: string
			jsonDetails: App.GuideTripDetailsJSON
		}

		export type RemoveGuideTripInput = {
			tripId: number
		}

		export type UpsertGuideTripMultiPeakInput = {
			// guideCompanyId: number
			id?: number
			price: number
			currency: string
			webLink: string
			trips: number[]
		}

		export type RemoveGuideTripMultiPeakInput = {
			guideTripripMultiPeakId: number
		}

		export type UpdateGuideProfile = {
			// guideId: number
			companyName: string
			// website: string
			jsonProfile: App.GuideJSONProfile
		}
	}

	export type UserUpload = {
		id: number
		isApproved: boolean
	}

	export type Article = DB.Models.ArticleAttributes & {
		createdAt: string
		updatedAt: string
	}

	export type Country = DB.Models.CountryAttributes & {
		createdAt: string
		updatedAt: string
	}

	export type Guide = DB.Models.GuideCompanyAttributes & {
		createdAt: string
		updatedAt: string
	}

	export type Upload = Omit<DB.Models.UploadAttributes, 'user'> & {
		user?: {
			guideCompany: {
				id: number
				companyName: string
			}
		} | null
	}

	export type PendingUpload = Pick<
		DB.Models.UploadAttributes,
		| 'id'
		| 'version'
		| 'uploadedBy'
		| 'source'
		| 'attributionText'
		| 'attributionLink'
		| 'resourceId'
		| 'resourceType'
	> & {
		tripName: string
	}

	export type GuideTrip = DB.Models.GuideTripAttributes & {
		trip?: DB.Models.TripAttributes | null
	}

	export type GuideTripMultiPeak = DB.Models.GuideTripMultiPeakAttributes & {
		trips?: DB.Models.TripAttributes[]
	}

	export type GuideCompany = DB.Models.GuideCompanyAttributes & {
		guideTrips: GuideTrip[]
		guideTripMultiPeaks: GuideTripMultiPeak[]
		uploads: Upload[]
		guideUploads: Upload[]
		certificates: Types.GuideCertificate[]
		updatedAt: string
		companyReviewData?: Types.CompanyReviewData | null
	}

	export type GuideTripOverview = Pick<
		DB.Models.TripAttributes,
		'id' | 'uniqueTripId' | 'version' | 'name' | 'isApproved' | 'publishedFrom'
	>

	export type GuideOverviewTripSummary = Omit<
		DB.Models.TripAttributes,
		'jsonData'
	> & {
		countries: string[]
		continent: string
	}

	// export type GuideTripSummary = DB.Models.GuideTripAttributes & {
	// 	trip?: GuideOverviewTripSummary
	// }

	// export type GuideTripMultiPeakSummary = DB.Models.GuideTripMultiPeakAttributes & {
	// 	trips?: GuideOverviewTripSummary[]
	// }

	export type LiteGuideTripSummary = {
		tripId: number
		name: string
		// slug: string
		price: number
		uploadId: number
		continent: string
		countries: string[]
	}

	export type LiteGuideTripMultiPeakSummary = {
		id: number
		price: number
		individualTrips: Omit<LiteGuideTripSummary, 'price'>[]
	}

	export type ReviewTasks = {
		total: number
		completed: number
	}

	export type GuideCompanyOverview = DB.Models.GuideCompanyAttributes & {
		guideTripsSummary: LiteGuideTripSummary[]
		guideTripMultiPeaksSummary: LiteGuideTripMultiPeakSummary[]
		// uploads: Upload[]
		countries: string[]
		certificates: Types.GuideCertificate[]
		reviewTasks?: ReviewTasks
	}

	export type AdminGuideCompanyOverview = {
		id: number
		name: string
		isOwned: boolean
		expeditions: number
		multiPeakTrips: number
		reviewUrls: string[]
		jsonProfile: App.GuideJSONProfile
		reviewTasks?: ReviewTasks
	}

	export namespace Reviews {
		export namespace GetGuideReviews {
			export type Input = {
				guideId: number
				page: number
				limit: number
				provider: App.DataTypes.Reviews.Provider | null
				keyword?: string
			}

			export type Response = {
				reviews: Array<{
					id: number
					provider: 'google' | 'tripadvisor' | 'trustpilot'
					reviewPercent: number
					// jsonReviewBlob: {
					// 	review_text?: string
					// 	text?: string
					// 	[key: string]: any
					// }
					jsonReviewBlob: string
					createdAt: string
				}>
				totalCount: number
				providerCounts: {
					google?: number
					tripadvisor?: number
					trustpilot?: number
				}
				keywordCounts: Array<KeywordCount>
			}
		}

		export interface KeywordCount {
			keyword: string
			count: number
			type: 'term' | 'peak'
		}
	}

	export type EmailTemplatePreview = {
		name: string
		params: string
		html: string
	}

	export namespace Types {
		export type GuideCertificate = {
			id: number
			imageId: number
			certificateName: string
			isApproved: boolean
		}

		export type GuideCertificateAdminView = Omit<
			GuideCertificate,
			'isApproved'
		> & {
			status: 'PENDING' | 'APPROVED' | 'REJECTED'
			guideCompanyId: number
			guideCompanyName: string
			createdAt: string
		}

		export type CompanyReviewData = DB.Models.CompanyReviewDataAttributes
	}

	export namespace Guide {
		export namespace UpdateGuide {
			export type UpdateGuideProfileInputUpdateObject = Partial<DB.Models.GuideCompanyAttributes>
			export type Input = {
				id: number
				updateGuideProfileInput: {
					updateObject: UpdateGuideProfileInputUpdateObject
				}
			}

			export type Response = {
				success: boolean
				guideScore?: App.Types.GuideScore
				error?: string
			}
		}

		export namespace TripInsights {
			export type Response = {
				success: boolean
				insights?: App.GuideInsights
			}
		}

		export namespace SubmitNewCertificate {
			export type Input = {
				imageId: number
				certificateName: string
			}
			export type Response = {
				success: boolean
				error?: string
			}
		}

		export namespace GetAllCertificates {
			export type Response = {
				success: boolean
				certificates?: API.Types.GuideCertificate[]
				error?: string
			}
		}

		export namespace RemoveCertificate {
			export type Input = {
				certificateId: number
			}

			export type Response = {
				success: boolean
				error?: string
			}
		}
		export namespace RequestReviewSync {
			export type Input = {
				guideCompanyId: number
			}

			export type Response = {
				success: boolean
				error?: string
			}
		}
	}

	export namespace Admin {
		export namespace GetAllCertificates {
			export type Input = {
				filter: {
					status: 'PENDING' | 'REJECTED' | 'APPROVED' | 'ALL'
				}
			}
			export type Response = {
				success: boolean
				certificates: API.Types.GuideCertificateAdminView[]
				error?: string
			}
		}

		export namespace ApproveCertificate {
			export type Input = {
				id: number
				name: string
			}
			export type Response = {
				success: boolean
				error?: string
			}
		}

		export namespace RejectCertificate {
			export type Input = {
				id: number
				rejectionReason: string
			}
			export type Response = {
				success: boolean
				error?: string
			}
		}

		export namespace Guides {
			export namespace GetAllGuides {
				export type Input = {
					filter: {
						name: string
					}
					pagination: API.PaginationInput
				}
				export type Response = {
					success: boolean
					error?: string
					collection: {
						items: API.AdminGuideCompanyOverview[]
						pageInfo: API.PaginationInfo
					}
				}
			}

			export namespace AddGuide {
				export type Input = {
					companyName: string
					website: string
					email: string
				}

				export type Response = {
					success: boolean
					error?: string
				}
			}

			export namespace GetGuideProfile {
				export type Input = {
					guideCompanyId: number
				}
				export type Response = {
					success: boolean
					guide?: API.GuideCompany
					error?: string
				}
			}

			export namespace UpdateGuideProfile {
				export type Input = {
					guideCompanyId: number
					updateObject: {
						companyName?: string
						jsonProfile?: App.GuideJSONProfile
					}
				}
				export type Response = {
					success: boolean
					error?: string
				}
			}

			export namespace ReviewProfiles {
				export namespace Get {
					export interface Input {
						guideCompanyId: number
					}

					export interface Response {
						success: boolean
						error?: string
						reviewProfiles?: {
							tripAdvisorReviewURL: string
							googleMapsReviewURL: string
							trustPilotReviewURL: string
						}
						companyReviewData?: DB.Models.CompanyReviewDataAttributes
						reviewTasks?: Array<{
							provider: string
							completed: boolean
						}>
					}
				}

				export namespace Update {
					export interface Input {
						guideCompanyId: number
						updateObject: {
							tripAdvisorReviewURL: string
							googleMapsReviewURL: string
							trustPilotReviewURL: string
						}
					}

					export interface Response {
						success: boolean
						error?: string
					}
				}
			}

			export namespace CreateGuideAccount {
				export type Input = {
					guideCompanyId: number
				}

				export type Response = {
					success: boolean
					error?: string
				}
			}

			export namespace GuideCompanyAction {
				export enum Action {
					RESET_PASSWORD = 'RESET_PASSWORD',
					DELETE_COMPANY = 'DELETE_COMPANY',
					REGENERATE_REVIEW_SUMMARY = 'REGENERATE_REVIEW_SUMMARY',
				}

				export type Input = {
					guideCompanyId: number
					action: string
					additionalData?: string
				}

				export type Response = {
					success: boolean
					error?: string
					message?: string
				}
			}
		}
	}

	export namespace Auth {
		export namespace RegisterViaGuard {
			export type Input = {
				email: string
				context?: string
			}
			export type Response = {
				success: boolean
				error?: string
				token?: string
				user?: App.PseudoUser
			}
		}
	}

	export namespace GuideOrAdmin {
		export namespace TripOfferings {
			export namespace GetTripOfferings {
				export type Input = {
					guideCompanyId: number
				}
				export type Response = {
					success: boolean
					error?: string
					collection: {
						items: App.TripOffering[]
					}
				}
			}

			export namespace UpdateTripOffering {
				export type Input = {
					guideCompanyId: number
					updateObject: {
						tripId: number
						price: number
						currency: string
						webLink: string
						jsonDetails: App.GuideTripDetailsJSON
					}
				}
				export type Response = {
					success: boolean
					error?: string
					collection: {
						items: App.TripOffering[]
					}
					guideScore?: App.Types.GuideScore
				}
			}

			export namespace RemoveTripOffering {
				export type Input = {
					guideCompanyId: number
					tripId: number
				}
				export type Response = {
					success: boolean
					error?: string
					collection: {
						items: App.TripOffering[]
					}
					guideScore?: App.Types.GuideScore
				}
			}
		}
	}
}
